/**
 * Nameplate aura icons (#137) — a curated MAJOR-aura row floating over each
 * unit's world nameplate. The DOM-overlay host of the reusable
 * {@link MajorAuraRow}: it reads the already-wired per-entity `auras`
 * (`WireEntity.auras`) from the sampled HUD snapshot and positions one chip row
 * per unit near its floating nameplate, via a world→screen `project` callback
 * (the live follow camera; MatchScreen supplies it, tests inject a stub).
 *
 * DISTINCT feeds (all reuse the ONE {@link AuraChip} tile):
 *   • #139 SelfAuraBar   — ALL of the VIEWER'S own auras, top-right.
 *   • #137 (this)        — curated MAJOR auras of OTHER units, on nameplates.
 *   • #138 UnitFrameStack— the same major row, per frame in the bottom stack.
 *
 * PURELY COSMETIC / CLIENT-SIDE. No sim, no protocol, no hash, no SIM_VERSION —
 * the overlay derives entirely from the HUD snapshot + the client camera. The
 * viewer's own unit is SUPPRESSED (its auras live on the top-right SelfAuraBar
 * and its own unit frame), mirroring the #136 self-nameplate suppression; dead
 * units get no row (mirrors #136 corpses).
 *
 * CADENCE CAVEAT: the overlay re-renders on the 10 Hz HUD sample, so chip
 * positions track the (30 Hz interpolated) bodies at snapshot cadence — a
 * deliberate cosmetic trade for a DOM overlay that reuses the shared chip. It is
 * anchored to the un-interpolated snapshot position, so it never diverges from
 * the authoritative unit location.
 *
 * data-* contract:
 *   • data-testid="nameplate-auras" — always present when enabled (empty when
 *     no snapshot / no unit has major auras)
 *   • data-count — number of unit rows rendered
 *   • per-unit anchor data-testid="nameplate-aura-anchor" (data-entity/data-seat)
 *   • each row is a <MajorAuraRow testId="nameplate-aura-row"> of
 *     <AuraChip testId="nameplate-aura-chip">
 */

import type { CSSProperties, ReactElement } from "react";
import { useStore } from "../store/context.js";
import { loadHudPrefs } from "../prefs.js";
import type { ScreenPos } from "../render/index.js";
// #878 §5: the world stack's own height, so this overlay's clearance is
// DERIVED from what it has to clear instead of being a hand-tuned magic
// number. A plain integer constant — importing it does not couple the overlay
// to any Pixi object (and `../render/index.js` already pulls that graph in for
// `ActionBar`/`MatchScreen`).
import { ABOVE_HEAD_STACK_HEIGHT_PX } from "../render/entityView.js";
import type { AuraCategory } from "../champions/auraIcons.js";
import {
  MajorAuraRow,
  majorAuras,
  DEFAULT_MAJOR_AURA_ROW_STYLE,
  type MajorAuraRowStyle,
} from "./MajorAuraRow.js";

/** Sim tick rate — matches MatchScreen / SelfAuraBar. */
const TICK_RATE = 30;

/**
 * World→screen projector for an entity origin. Supplied by MatchScreen (wraps
 * the live follow-camera `worldToScreen`); injected in tests so the overlay is
 * exercised without a Pixi renderer.
 */
export type EntityProjector = (x: number, y: number) => ScreenPos;

/** Layout treatment for the nameplate aura overlay. */
export interface NameplateAurasStyle {
  /** px the row's BOTTOM edge floats ABOVE the projected entity origin (the
   *  row is `translate(-50%, -100%)`-anchored). Clears the above-head bar
   *  stack + the #136 hotkey label — see {@link NAMEPLATE_AURA_HEADROOM_PX}. */
  readonly offsetAbovePx: number;
  /** Max icons per nameplate row (keeps a crowded nameplate readable). */
  readonly maxIcons: number;
  readonly row: MajorAuraRowStyle;
}

/**
 * Clear space (px) this chip row's BOTTOM edge keeps above the full height of
 * the world above-head stack — the spell-name row + three bars the #136
 * target-hotkey nameplate is itself anchored on top of (#878 §5).
 *
 * WHY THIS EXISTS: `offsetAbovePx` used to be a hand-tuned `34` with NO
 * relationship to the stack it is supposed to clear, so any retune of the bar
 * stack could silently push the nameplate up into the chips — the overlap the
 * owner asked #878 to make impossible. It is now DERIVED
 * (`ABOVE_HEAD_STACK_HEIGHT_PX + NAMEPLATE_AURA_HEADROOM_PX`): a taller stack
 * raises this row by exactly as much, automatically, and the dependency is a
 * compile-time one a future retune cannot miss.
 *
 * `13` is chosen so the RENDERED position is byte-identical to the pre-#878
 * `34` (the #878 stack is 21px tall): this change makes the dependency
 * explicit, it deliberately does NOT move the row.
 *
 * KNOWN RESIDUAL, deliberately unchanged here: the two systems are not in the
 * same space — this overlay is DOM/screen-space anchored on the projected
 * entity origin, while the stack is drawn inside the zoom-scaled entities
 * layer and is anchored on the render path's own base (`barStackBaseY`, much
 * larger for a 3D model than for the 2D body). So the absolute px above the
 * origin are not directly comparable, and the nameplate's own GLYPH BOX still
 * rises above the chip row's bottom edge exactly as it did before #878.
 * Retuning the overlay's position is #137's call and explicitly out of scope
 * for #878, which is why the number is preserved rather than "fixed" here.
 */
export const NAMEPLATE_AURA_HEADROOM_PX = 13;

/** Default treatment. */
export const DEFAULT_NAMEPLATE_AURAS_STYLE: NameplateAurasStyle = {
  offsetAbovePx: ABOVE_HEAD_STACK_HEIGHT_PX + NAMEPLATE_AURA_HEADROOM_PX,
  maxIcons: 5,
  row: DEFAULT_MAJOR_AURA_ROW_STYLE,
};

export interface NameplateAurasProps {
  /** World→screen projector (live follow camera). */
  readonly project: EntityProjector;
  /** Selected "major" categories. Defaults to the prefs selection (#137). */
  readonly categories?: readonly AuraCategory[];
  /** Feature toggle. Defaults to the prefs toggle (#137). */
  readonly enabled?: boolean;
  readonly style?: NameplateAurasStyle;
}

export function NameplateAuras({
  project,
  categories,
  enabled,
  style = DEFAULT_NAMEPLATE_AURAS_STYLE,
}: NameplateAurasProps): ReactElement | null {
  const { state } = useStore();
  const hud = loadHudPrefs();
  const on = enabled ?? hud.nameplateAuras;
  if (!on) return null;

  const cats = categories ?? hud.nameplateAuraCategories;
  const snapshot = state.hudSnapshot;
  const ownSeat = state.ownSeat;
  const now = snapshot?.tick ?? 0;

  const rows: ReactElement[] = [];
  if (snapshot !== null) {
    // Canonical seat order (stable; matches the sim's seat iteration).
    const entities = [...snapshot.entities].sort((a, b) => a.seat - b.seat);
    for (const e of entities) {
      // Corpses: no aura row (mirror the #136 nameplate corpse rule).
      if (!e.alive) continue;
      // Self is covered by the top-right SelfAuraBar + its own unit frame.
      if (ownSeat !== null && e.seat === ownSeat) continue;
      const shown = majorAuras(e.auras, cats, now, style.maxIcons);
      if (shown.length === 0) continue;
      const p = project(e.x, e.y);
      const anchor: CSSProperties = {
        position: "absolute",
        left: p.sx,
        top: p.sy - style.offsetAbovePx,
        transform: "translate(-50%, -100%)",
        pointerEvents: "none",
      };
      rows.push(
        <div
          key={e.entityId}
          data-testid="nameplate-aura-anchor"
          data-entity={e.entityId}
          data-seat={String(e.seat)}
          style={anchor}
        >
          <MajorAuraRow
            auras={e.auras}
            now={now}
            tickRate={TICK_RATE}
            categories={cats}
            max={style.maxIcons}
            style={style.row}
            testId="nameplate-aura-row"
            chipTestId="nameplate-aura-chip"
          />
        </div>,
      );
    }
  }

  const container: CSSProperties = {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
  };

  return (
    <div data-testid="nameplate-auras" data-count={String(rows.length)} style={container}>
      {rows}
    </div>
  );
}
